import React, { useState, useMemo, useCallback, useRef } from 'react';
import { Radio, Card, InputNumber, Statistic, Divider, Dropdown, Button, Select, Tag, Modal, Input, List, message, Popconfirm } from 'antd';
import { PlusOutlined, CloseOutlined, SaveOutlined, FolderOpenOutlined, DeleteOutlined, PlusCircleOutlined, ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';
import FabricChain from './FabricChain';
import type { FabricConfig, FabricType, ChainStage, StageType } from './types';
import { STAGE_LABELS } from './types';
import { calculate } from './formulas';
import { costcalcApi, type CostQuote } from '../../services/costcalcApi';

/** 過濾非數字字元（允許小數點） */
const numParser = (val: string | undefined) => Number(val?.replace(/[^\d.]/g, '') || 0);

let _defaultPlId = 500; // fallback for module-level calls before component mounts

const newFabric = (type: FabricType = 'woven'): FabricConfig => ({
  type,
  fabricCode: '',
  rawFabricCode: '',
  basePrice: 29,
  baseLossRate: 0,
  processingFee: 7,
  fabricWeight: 320,
  yarns: type === 'knit'
    ? [
        { id: 'y1', code: 'YP0183', price: 85, ratio: 32.9 },
        { id: 'y2', code: 'YP0095', price: 60, ratio: 67.1 },
      ]
    : [],
  stages: [],
});

// 貼合後可選工段（梭織規則）
const POST_LAM_MENU: StageType[] = ['dyeing', 'chemical', 'brushing', 'inspection', 'printing', 'calendering', 'lamination'];

const POST_LAM_DEFAULTS: Record<StageType, Omit<ChainStage, 'id' | 'type'>> = {
  knitting:    { cost: 0,    lossRate: 0,  inspectionFee: 0 },
  dyeing:      { cost: 16,   lossRate: 18, inspectionFee: 0 },
  chemical:    { cost: 11.5, lossRate: 0,  inspectionFee: 0 },
  brushing:    { cost: 8,    lossRate: 2,  inspectionFee: 1.5 },
  inspection:  { cost: 1.5,  lossRate: 0,  inspectionFee: 0 },
  printing:    { cost: 25,   lossRate: 3,  inspectionFee: 3.7 },
  calendering: { cost: 12,   lossRate: 2,  inspectionFee: 1.2 },
  lamination:  { cost: 40,   lossRate: 5,  inspectionFee: 1.5 },
};

interface SupplierPreset { label: string; cost: number; lossRate: number; inspectionFee: number; }
const POST_LAM_SUPPLIERS: Partial<Record<StageType, SupplierPreset[]>> = {
  dyeing: [
    { label: '預設',    cost: 16, lossRate: 18, inspectionFee: 0 },
    { label: '供應商B', cost: 18, lossRate: 15, inspectionFee: 0 },
  ],
  inspection: [
    { label: '預設 1.5/YD', cost: 1.5, lossRate: 0, inspectionFee: 0 },
    { label: '2.0/YD',      cost: 2.0, lossRate: 0, inspectionFee: 0 },
  ],
  printing: [
    { label: '預設',    cost: 25, lossRate: 3, inspectionFee: 3.7 },
    { label: '供應商B', cost: 30, lossRate: 2, inspectionFee: 3.7 },
  ],
  calendering: [
    { label: '預設',    cost: 12, lossRate: 2, inspectionFee: 1.2 },
    { label: '供應商B', cost: 15, lossRate: 2, inspectionFee: 1.2 },
  ],
  lamination: [
    { label: '2L 貼合',   cost: 40, lossRate: 5, inspectionFee: 1.5 },
    { label: '2.5L 貼合', cost: 45, lossRate: 5, inspectionFee: 1.5 },
    { label: '3L 貼合',   cost: 55, lossRate: 5, inspectionFee: 1.5 },
  ],
};

/** 將所有 state 序列化為 JSON */
interface SavedConfig {
  fabricCount: 1 | 2 | 3;
  fabric1: FabricConfig;
  fabric2: FabricConfig;
  fabric3: FabricConfig;
  lam1Cost: number; lam1Cost2: number; lam1Loss: number;
  postLam1Stages: ChainStage[];
  lam2Cost: number; lam2Cost2: number; lam2Loss: number;
  postLamStages: ChainStage[];
  expenseRate: number; profitMargin: number; commissionRate: number; exchangeRate: number;
}

const CostCalculator: React.FC = () => {
  const plIdRef = useRef(_defaultPlId);
  const nextPlId = useCallback(() => `pl${++plIdRef.current}`, []);

  const [fabricCount, setFabricCount] = useState<1 | 2 | 3>(1);
  const [fabric1, setFabric1] = useState<FabricConfig>(newFabric('woven'));
  const [fabric2, setFabric2] = useState<FabricConfig>(newFabric('knit'));
  const [fabric3, setFabric3] = useState<FabricConfig>(newFabric('woven'));

  // 第一次貼合參數
  const [lam1Cost, setLam1Cost] = useState(45);
  const [lam1Cost2, setLam1Cost2] = useState(0);
  const [lam1Loss, setLam1Loss] = useState(5);

  // 第一次貼合後工段（4層：雙層→三層之間）
  const [postLam1Stages, setPostLam1Stages] = useState<ChainStage[]>([]);

  // 第二次貼合參數（4層用）
  const [lam2Cost, setLam2Cost] = useState(45);
  const [lam2Cost2, setLam2Cost2] = useState(0);
  const [lam2Loss, setLam2Loss] = useState(5);

  // 貼合後工段
  const [postLamStages, setPostLamStages] = useState<ChainStage[]>([]);

  // 報價參數
  const [expenseRate, setExpenseRate] = useState(5);
  const [profitMargin, setProfitMargin] = useState(25);
  const [commissionRate, setCommissionRate] = useState(0);
  const [exchangeRate, setExchangeRate] = useState(31);

  // ===== 儲存/載入 =====
  const [currentQuoteId, setCurrentQuoteId] = useState<number | null>(null);
  const [quoteName, setQuoteName] = useState('');
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [loadModalOpen, setLoadModalOpen] = useState(false);
  const [quoteList, setQuoteList] = useState<CostQuote[]>([]);
  const [loadingList, setLoadingList] = useState(false);
  const [saving, setSaving] = useState(false);

  const collectConfig = useCallback((): SavedConfig => ({
    fabricCount, fabric1, fabric2, fabric3,
    lam1Cost, lam1Cost2, lam1Loss, postLam1Stages,
    lam2Cost, lam2Cost2, lam2Loss, postLamStages,
    expenseRate, profitMargin, commissionRate, exchangeRate,
  }), [fabricCount, fabric1, fabric2, fabric3,
    lam1Cost, lam1Cost2, lam1Loss, postLam1Stages,
    lam2Cost, lam2Cost2, lam2Loss, postLamStages,
    expenseRate, profitMargin, commissionRate, exchangeRate]);

  const applyConfig = (cfg: Partial<SavedConfig>) => {
    setFabricCount(cfg.fabricCount ?? 1);
    setFabric1(cfg.fabric1 ?? newFabric('woven'));
    setFabric2(cfg.fabric2 ?? newFabric('knit'));
    setFabric3(cfg.fabric3 ?? newFabric('woven'));
    setLam1Cost(cfg.lam1Cost ?? 45); setLam1Cost2(cfg.lam1Cost2 ?? 0); setLam1Loss(cfg.lam1Loss ?? 5);
    setPostLam1Stages(cfg.postLam1Stages ?? []);
    setLam2Cost(cfg.lam2Cost ?? 45); setLam2Cost2(cfg.lam2Cost2 ?? 0); setLam2Loss(cfg.lam2Loss ?? 5);
    setPostLamStages(cfg.postLamStages ?? []);
    setExpenseRate(cfg.expenseRate ?? 5); setProfitMargin(cfg.profitMargin ?? 25);
    setCommissionRate(cfg.commissionRate ?? 0); setExchangeRate(cfg.exchangeRate ?? 31);
  };

  const handleSave = async () => {
    if (!quoteName.trim()) { message.warning('請輸入報價名稱'); return; }
    if (saving) return;
    setSaving(true);
    try {
      const configJson = JSON.stringify(collectConfig());
      if (currentQuoteId) {
        await costcalcApi.update(currentQuoteId, { quoteName, mode: fabricCount, config: configJson });
        message.success('已更新');
      } else {
        const created = await costcalcApi.create({ quoteName, mode: fabricCount, config: configJson });
        setCurrentQuoteId(created.id);
        message.success('已儲存');
      }
      setSaveModalOpen(false);
    } catch { message.error('儲存失敗'); }
    setSaving(false);
  };

  const handleLoad = async () => {
    setLoadingList(true);
    try {
      const res = await costcalcApi.list(1, 50);
      setQuoteList(res.data || []);
    } catch { message.error('載入清單失敗'); }
    setLoadingList(false);
    setLoadModalOpen(true);
  };

  const handleLoadItem = (item: CostQuote) => {
    try {
      const cfg: SavedConfig = JSON.parse(item.config);
      applyConfig(cfg);
      setCurrentQuoteId(item.id);
      setQuoteName(item.quoteName);
      setLoadModalOpen(false);
      message.success(`已載入: ${item.quoteName}`);
    } catch { message.error('解析報價資料失敗'); }
  };

  const handleDeleteItem = async (id: number) => {
    try {
      await costcalcApi.delete(id);
      setQuoteList(prev => prev.filter(q => q.id !== id));
      if (currentQuoteId === id) { setCurrentQuoteId(null); setQuoteName(''); }
      message.success('已刪除');
    } catch { message.error('刪除失敗'); }
  };

  const handleNew = () => {
    setCurrentQuoteId(null);
    setQuoteName('');
    setFabricCount(1);
    setFabric1(newFabric('woven'));
    setFabric2(newFabric('knit'));
    setFabric3(newFabric('woven'));
    setLam1Cost(45); setLam1Cost2(0); setLam1Loss(5);
    setPostLam1Stages([]);
    setLam2Cost(45); setLam2Cost2(0); setLam2Loss(5);
    setPostLamStages([]);
    setExpenseRate(5); setProfitMargin(25); setCommissionRate(0); setExchangeRate(31);
  };

  const result = useMemo(
    () => calculate({
      fabricCount,
      fabric1,
      fabric2: fabricCount >= 2 ? fabric2 : undefined,
      fabric3: fabricCount >= 3 ? fabric3 : undefined,
      lam1Cost, lam1Cost2, lam1LossRate: lam1Loss,
      postLam1Stages: fabricCount >= 3 ? postLam1Stages : [],
      lam2Cost, lam2Cost2, lam2LossRate: lam2Loss,
      postLamStages: fabricCount >= 2 ? postLamStages : [],
      expenseRate, profitMargin, commissionRate, exchangeRate,
    }),
    [fabricCount, fabric1, fabric2, fabric3,
     lam1Cost, lam1Cost2, lam1Loss, postLam1Stages,
     lam2Cost, lam2Cost2, lam2Loss,
     postLamStages, expenseRate, profitMargin, commissionRate, exchangeRate],
  );

  // 第一次貼合後工段操作（4層模式）
  const addPostLam1Stage = (type: StageType, insertAfterIndex?: number) => {
    const d = POST_LAM_DEFAULTS[type];
    const newStage = { id: nextPlId(), type, ...d };
    setPostLam1Stages(prev => {
      if (insertAfterIndex !== undefined) {
        const arr = [...prev];
        arr.splice(insertAfterIndex + 1, 0, newStage);
        return arr;
      }
      return [...prev, newStage];
    });
  };
  const updatePostLam1Stage = (id: string, patch: Partial<ChainStage>) => {
    setPostLam1Stages(prev => prev.map(s => (s.id === id ? { ...s, ...patch } : s)));
  };
  const removePostLam1Stage = (id: string) => {
    setPostLam1Stages(prev => prev.filter(s => s.id !== id));
  };
  const movePostLam1Stage = (stageId: string, direction: 'up' | 'down') => {
    setPostLam1Stages(prev => {
      const stages = [...prev];
      const idx = stages.findIndex(s => s.id === stageId);
      if (idx < 0) return prev;
      if (direction === 'up' && idx === 0) return prev;
      if (direction === 'down' && idx === stages.length - 1) return prev;
      const targetIdx = direction === 'up' ? idx - 1 : idx + 1;
      [stages[idx], stages[targetIdx]] = [stages[targetIdx], stages[idx]];
      return stages;
    });
  };
  const postLam1Menu = {
    items: POST_LAM_MENU.map(t => ({
      key: t, label: STAGE_LABELS[t], onClick: () => addPostLam1Stage(t),
    })),
  };

  // 最終貼合後工段操作
  const addPostLamStage = (type: StageType, insertAfterIndex?: number) => {
    const d = POST_LAM_DEFAULTS[type];
    const newStage = { id: nextPlId(), type, ...d };
    setPostLamStages(prev => {
      if (insertAfterIndex !== undefined) {
        const arr = [...prev];
        arr.splice(insertAfterIndex + 1, 0, newStage);
        return arr;
      }
      return [...prev, newStage];
    });
  };
  const updatePostLamStage = (id: string, patch: Partial<ChainStage>) => {
    setPostLamStages(prev => prev.map(s => (s.id === id ? { ...s, ...patch } : s)));
  };
  const removePostLamStage = (id: string) => {
    setPostLamStages(prev => prev.filter(s => s.id !== id));
  };
  const movePostLamStage = (stageId: string, direction: 'up' | 'down') => {
    setPostLamStages(prev => {
      const stages = [...prev];
      const idx = stages.findIndex(s => s.id === stageId);
      if (idx < 0) return prev;
      if (direction === 'up' && idx === 0) return prev;
      if (direction === 'down' && idx === stages.length - 1) return prev;
      const targetIdx = direction === 'up' ? idx - 1 : idx + 1;
      [stages[idx], stages[targetIdx]] = [stages[targetIdx], stages[idx]];
      return stages;
    });
  };
  const postLamMenu = {
    items: POST_LAM_MENU.map(t => ({
      key: t, label: STAGE_LABELS[t], onClick: () => addPostLamStage(t),
    })),
  };

  // 渲染貼合後工段卡片（共用，透過參數切換 postLam1 / postLam）
  const makeRenderStage = (
    results: typeof result.postLamResults,
    onUpdate: typeof updatePostLamStage,
    onRemove: typeof removePostLamStage,
    onInsertAfter: (type: StageType, index: number) => void,
    onMove: (stageId: string, direction: 'up' | 'down') => void,
    totalStages: number,
  ) => (stage: ChainStage, stageIndex: number) => {
    const r = results.find(s => s.id === stage.id);
    const isChem = stage.type === 'chemical';
    const isInsp = stage.type === 'inspection';
    const presets = POST_LAM_SUPPLIERS[stage.type];
    const applyPreset = (idx: number) => {
      if (!presets?.length) return;
      const p = presets[idx];
      if (p) onUpdate(stage.id, { cost: p.cost, lossRate: p.lossRate, inspectionFee: p.inspectionFee });
    };

    const insertMenu = {
      items: POST_LAM_MENU.map(t => ({
        key: t,
        label: STAGE_LABELS[t],
        onClick: () => onInsertAfter(t, stageIndex),
      })),
    };

    return (
      <React.Fragment key={stage.id}>
        <Card size="small" className="mb-1" style={{ backgroundColor: '#dcfce7', borderColor: '#86efac' }}>
          <div className="flex items-center justify-center gap-2 mb-1">
            <span className="font-medium text-sm">{STAGE_LABELS[stage.type]}</span>
            {r && r.lossAmount > 0 && <Tag color="orange" className="text-xs">損耗 +{r.lossAmount.toFixed(2)}</Tag>}
            {r && <Tag color="blue" className="text-xs">累計 {r.subtotal.toFixed(2)} YD</Tag>}
            <Button size="small" type="text" icon={<ArrowUpOutlined />}
              disabled={stageIndex === 0}
              onClick={() => onMove(stage.id, 'up')}
              style={{ padding: '0 4px' }} />
            <Button size="small" type="text" icon={<ArrowDownOutlined />}
              disabled={stageIndex === totalStages - 1}
              onClick={() => onMove(stage.id, 'down')}
              style={{ padding: '0 4px' }} />
            <Button size="small" type="text" danger icon={<CloseOutlined />} onClick={() => onRemove(stage.id)} />
          </div>
          <div className="flex flex-wrap gap-3 items-end justify-center">
            {presets && presets.length > 0 && (
              <div>
                <div className="text-xs text-gray-500">供應商</div>
                <Select size="small" style={{ width: 110 }} placeholder="選擇"
                  onChange={applyPreset}
                  options={presets.map((p, i) => ({ label: p.label, value: i }))} />
              </div>
            )}
            {isChem && (
              <div>
                <div className="text-xs text-gray-500">藥劑產品</div>
                <Input size="small" style={{ width: 110 }} placeholder="產品名稱"
                  value={stage.chemicalProduct || ''}
                  onChange={e => onUpdate(stage.id, { chemicalProduct: e.target.value })} />
              </div>
            )}
            {isInsp ? (
              <div>
                <div className="text-xs text-gray-500">費用 (/YD)</div>
                <InputNumber size="small" parser={numParser} value={stage.cost} min={0} step={0.1} style={{ width: 85 }}
                  onChange={v => onUpdate(stage.id, { cost: v ?? 0 })} />
              </div>
            ) : (
              <>
                <div>
                  <div className="text-xs text-gray-500">費用</div>
                  <InputNumber size="small" parser={numParser} value={stage.cost} min={0} step={0.5} style={{ width: 85 }}
                    onChange={v => onUpdate(stage.id, { cost: v ?? 0 })} />
                </div>
                <div>
                  <div className="text-xs text-gray-500">{isChem ? '自身損耗%' : '損耗率%'}</div>
                  <InputNumber size="small" parser={numParser} value={stage.lossRate} min={0} max={99} step={0.5} style={{ width: 80 }}
                    onChange={v => onUpdate(stage.id, { lossRate: v ?? 0 })} />
                </div>
                {!isChem && (
                  <div>
                    <div className="text-xs text-gray-500">驗貨費</div>
                    <InputNumber size="small" parser={numParser} value={stage.inspectionFee} min={0} step={0.1} style={{ width: 75 }}
                      onChange={v => onUpdate(stage.id, { inspectionFee: v ?? 0 })} />
                  </div>
                )}
              </>
            )}
          </div>
        </Card>
        <Dropdown menu={insertMenu} trigger={['click']}>
          <div
            style={{ display: 'flex', alignItems: 'center', height: 20, marginBottom: 4, cursor: 'pointer' }}
            className="group"
          >
            <div style={{ flex: 1, height: 1, borderTop: '1px dashed #d1d5db' }} />
            <span style={{ fontSize: 11, color: '#9ca3af', padding: '0 6px', whiteSpace: 'nowrap' }}
              className="group-hover:text-green-600">
              <PlusCircleOutlined style={{ fontSize: 11, marginRight: 3 }} />插入工段
            </span>
            <div style={{ flex: 1, height: 1, borderTop: '1px dashed #d1d5db' }} />
          </div>
        </Dropdown>
      </React.Fragment>
    );
  };

  // 渲染貼合區塊（共用）
  const renderLamination = (
    title: string, costBefore: string,
    cost: number, setCost: (v: number) => void,
    cost2: number, setCost2: (v: number) => void,
    loss: number, setLoss: (v: number) => void,
    lamResult: number,
  ) => (
    <Card size="small" className="mt-4" style={{ backgroundColor: '#fee2e2', borderColor: '#fca5a5' }}>
      <div className="flex flex-col items-center mb-2">
        <span className="font-bold text-sm">{title}</span>
        <span className="text-xs text-gray-500">
          {costBefore}
          {loss > 0 ? ` / (1-${loss}%)` : ''}
          {` + ${(cost + cost2).toFixed(1)}`}
          {` = ${lamResult.toFixed(2)}`}
        </span>
      </div>
      <div className="flex gap-4 items-end justify-center">
        <div>
          <div className="text-xs text-gray-500">貼合費</div>
          <InputNumber size="small" parser={numParser} value={cost} min={0} step={1} style={{ width: 85 }}
            onChange={v => setCost(v ?? 0)} />
        </div>
        <div>
          <div className="text-xs text-gray-500">貼合費 2</div>
          <InputNumber size="small" parser={numParser} value={cost2} min={0} step={1} style={{ width: 85 }}
            onChange={v => setCost2(v ?? 0)} />
        </div>
        <div>
          <div className="text-xs text-gray-500">損耗%</div>
          <InputNumber size="small" parser={numParser} value={loss} min={0} max={99} step={0.5} style={{ width: 70 }}
            onChange={v => setLoss(v ?? 0)} />
        </div>
      </div>
    </Card>
  );

  const isMulti = fabricCount >= 2;

  return (
    <div className="p-4 max-w-6xl">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold">成本計算機</h2>
        <div className="flex gap-2 items-center">
          {currentQuoteId && (
            <Tag color="blue" className="text-sm">{quoteName} (#{currentQuoteId})</Tag>
          )}
          <Button size="small" onClick={handleNew}>新增報價</Button>
          <Button size="small" icon={<FolderOpenOutlined />} onClick={handleLoad}>載入</Button>
          <Button size="small" type="primary" icon={<SaveOutlined />}
            onClick={() => setSaveModalOpen(true)}>儲存</Button>
        </div>
      </div>

      {/* 儲存 Modal */}
      <Modal title="儲存報價" open={saveModalOpen} onOk={handleSave}
        onCancel={() => setSaveModalOpen(false)} okText="儲存" cancelText="取消"
        confirmLoading={saving}>
        <div className="mb-2">
          <div className="text-sm text-gray-500 mb-1">報價名稱</div>
          <Input value={quoteName} onChange={e => setQuoteName(e.target.value)}
            placeholder="例: PLY476 三層貼合報價" />
        </div>
      </Modal>

      {/* 載入 Modal */}
      <Modal title="載入報價" open={loadModalOpen} footer={null}
        onCancel={() => setLoadModalOpen(false)} width={500}>
        <List
          loading={loadingList}
          dataSource={quoteList}
          locale={{ emptyText: '沒有已儲存的報價' }}
          renderItem={item => (
            <List.Item
              actions={[
                <Button size="small" type="link" onClick={() => handleLoadItem(item)}>載入</Button>,
                <Popconfirm title="確定刪除？" onConfirm={() => handleDeleteItem(item.id)}>
                  <Button size="small" type="link" danger icon={<DeleteOutlined />} />
                </Popconfirm>,
              ]}
            >
              <List.Item.Meta
                title={item.quoteName}
                description={`模式: ${item.mode === 1 ? '單層' : item.mode === 2 ? '對貼' : '4層'} | ${new Date(item.createdAt).toLocaleDateString()}`}
              />
            </List.Item>
          )}
        />
      </Modal>

      {/* 模式選擇 */}
      <div className="mb-4">
        <Radio.Group value={fabricCount} onChange={e => setFabricCount(e.target.value)} buttonStyle="solid" size="small">
          <Radio.Button value={1}>單層/2 layer</Radio.Button>
          <Radio.Button value={2}>對貼/3層貼合</Radio.Button>
          <Radio.Button value={3}>4層貼合</Radio.Button>
        </Radio.Group>
      </div>

      {/* 第一層 + 第二層 */}
      <div className={isMulti ? 'grid grid-cols-2 gap-4' : 'max-w-xl mx-auto'}>
        <FabricChain
          label={isMulti ? '第一層' : undefined}
          config={fabric1}
          result={result.fabric1}
          onChange={setFabric1}
          className="bg-yellow-100 rounded-lg p-3 border border-yellow-300"
        />
        {isMulti && result.fabric2 && (
          <FabricChain
            label="第二層"
            config={fabric2}
            result={result.fabric2}
            onChange={setFabric2}
            className="bg-blue-100 rounded-lg p-3 border border-blue-300"
          />
        )}
      </div>

      {/* 第一次貼合 (L1 + L2) + 後續工段 */}
      {isMulti && (
        <>
          {renderLamination(
            '貼合 (雙層)',
            `(${result.fabric1.totalCostYd.toFixed(2)} + ${result.fabric2?.totalCostYd.toFixed(2) ?? '0'})`,
            lam1Cost, setLam1Cost,
            lam1Cost2, setLam1Cost2,
            lam1Loss, setLam1Loss,
            result.lam1Cost,
          )}

          {/* 第一次貼合後工段（對貼模式 = 最終工段；4層模式 = 中間工段） */}
          {fabricCount === 2 && (
            <Card size="small" className="mt-2" style={{ backgroundColor: '#fee2e2', borderColor: '#fca5a5' }}>
              <div className="mt-1">
                {postLamStages.map(makeRenderStage(result.postLamResults, updatePostLamStage, removePostLamStage, addPostLamStage, movePostLamStage, postLamStages.length))}
                <Dropdown menu={postLamMenu} trigger={['click']}>
                  <Button size="small" type="dashed" icon={<PlusOutlined />} className="w-full">
                    新增工段
                  </Button>
                </Dropdown>
              </div>
            </Card>
          )}

          {fabricCount === 3 && (
            <Card size="small" className="mt-2" style={{ backgroundColor: '#fee2e2', borderColor: '#fca5a5' }}>
              <div className="mt-1">
                {postLam1Stages.map(makeRenderStage(result.postLam1Results, updatePostLam1Stage, removePostLam1Stage, addPostLam1Stage, movePostLam1Stage, postLam1Stages.length))}
                <Dropdown menu={postLam1Menu} trigger={['click']}>
                  <Button size="small" type="dashed" icon={<PlusOutlined />} className="w-full">
                    新增工段
                  </Button>
                </Dropdown>
              </div>
            </Card>
          )}
        </>
      )}

      {/* 4層模式：第三層 + 第二次貼合 + 最終工段 */}
      {fabricCount === 3 && (
        <>
          <div className="mt-4 max-w-xl mx-auto">
            <FabricChain
              label="第三層"
              config={fabric3}
              result={result.fabric3!}
              onChange={setFabric3}
              className="bg-purple-100 rounded-lg p-3 border border-purple-300"
            />
          </div>

          {renderLamination(
            '貼合 (三層)',
            `(${result.afterPostLam1Cost.toFixed(2)} + ${result.fabric3?.totalCostYd.toFixed(2) ?? '0'})`,
            lam2Cost, setLam2Cost,
            lam2Cost2, setLam2Cost2,
            lam2Loss, setLam2Loss,
            result.lam2Cost,
          )}

          <Card size="small" className="mt-2" style={{ backgroundColor: '#fee2e2', borderColor: '#fca5a5' }}>
            <div className="mt-1">
              {postLamStages.map(makeRenderStage(result.postLamResults, updatePostLamStage, removePostLamStage, addPostLamStage, movePostLamStage, postLamStages.length))}
              <Dropdown menu={postLamMenu} trigger={['click']}>
                <Button size="small" type="dashed" icon={<PlusOutlined />} className="w-full">
                  新增工段
                </Button>
              </Dropdown>
            </div>
          </Card>
        </>
      )}

      {/* ===== 報價 ===== */}
      <Card size="small" className="mt-4" style={{ backgroundColor: '#dcfce7', borderColor: '#86efac' }}>
        <div className="flex flex-wrap gap-6 items-end justify-center">
          <div>
            <div className="text-xs text-gray-500">費用率 %</div>
            <InputNumber size="small" parser={numParser} value={expenseRate} min={0} max={99} step={0.5} style={{ width: 80 }}
              onChange={v => setExpenseRate(v ?? 0)} />
          </div>
          <div>
            <div className="text-xs text-gray-500">毛利率 %</div>
            <InputNumber size="small" parser={numParser} value={profitMargin} min={0} max={99} step={0.5} style={{ width: 80 }}
              onChange={v => setProfitMargin(v ?? 0)} />
          </div>
          <div>
            <div className="text-xs text-gray-500">佣金率 %</div>
            <InputNumber size="small" parser={numParser} value={commissionRate} min={0} max={99} step={0.5} style={{ width: 80 }}
              onChange={v => setCommissionRate(v ?? 0)} />
          </div>
          <div>
            <div className="text-xs text-gray-500">匯率 (TWD/USD)</div>
            <InputNumber size="small" parser={numParser} value={exchangeRate} min={0.01} step={0.5} style={{ width: 90 }}
              onChange={v => setExchangeRate(v ?? 1)} />
          </div>
        </div>
        <Divider className="my-3" />
        <div className="flex gap-8 justify-center">
          <Statistic title="總成本" value={result.combinedCost} precision={2} suffix="TWD/YD" valueStyle={{ fontSize: 20 }} />
          <Statistic title="售價" value={result.sellingPrice} precision={2} suffix="USD"
            valueStyle={{ fontSize: 24, color: '#1890ff', fontWeight: 'bold' }} />
          <Statistic title="報價 (四捨五入)" value={result.roundedPrice} precision={2} suffix="USD"
            valueStyle={{ fontSize: 20, color: '#52c41a' }} />
        </div>
      </Card>
    </div>
  );
};

export default CostCalculator;
